import { useState, useCallback, useRef } from 'react';
import type { Manager } from 'coco-cashu-core';
import { useToast } from '@/hooks/useToast';

interface UseLightningOperationsProps {
  coco: Manager | null;
  activeMintUrl: string | null;
  generateQRCode: (text: string) => Promise<string>;
}

export function useLightningOperations({
  coco,
  activeMintUrl,
  generateQRCode,
}: UseLightningOperationsProps) {
  const { toast } = useToast();
  
  // Receive state
  const [quoteAmount, setQuoteAmount] = useState('');
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState<{
    quoteId: string;
    mintUrl: string;
    amount: number;
    invoice: string;
    qrCodeUrl: string;
    expiry: number;
  } | null>(null);

  // Receive-generation in-flight flag (so the button can show progress and
  // can't be double-tapped into two invoices).
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);

  // Send state
  const [lightningInvoice, setLightningInvoice] = useState('');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  const mintQuoteUnsubscribeRef = useRef<(() => void) | null>(null);

  // Handle receive (create mint quote/invoice)
  const handleLightningReceive = useCallback(async () => {
    if (isGeneratingInvoice || !coco || !quoteAmount || !activeMintUrl) return;

    try {
      const amount = parseInt(quoteAmount);
      if (isNaN(amount) || amount <= 0) {
        toast({ 
          variant: 'destructive', 
          title: 'Invalid amount', 
          description: 'Please enter a valid amount' 
        });
        return;
      }

      setIsGeneratingInvoice(true);
      
      const quote = await coco.quotes.createMintQuote(activeMintUrl, amount);
      const qrCodeUrl = await generateQRCode(quote.request);

      setCurrentInvoice({
        quoteId: quote.quote,
        mintUrl: activeMintUrl,
        amount,
        invoice: quote.request,
        qrCodeUrl,
        expiry: quote.expiry,
      });
      
      setShowInvoiceModal(true);
      setQuoteAmount('');

      // Clean up any previous listener
      if (mintQuoteUnsubscribeRef.current) {
        mintQuoteUnsubscribeRef.current();
        mintQuoteUnsubscribeRef.current = null;
      }

      // Listen for payment
      const quoteId = quote.quote;
      const invoiceAmount = amount;

      const unsubscribe = coco.on('mint-quote:redeemed', (payload: unknown) => {
        const redeemedQuote = payload as { quoteId?: string };

        if (redeemedQuote.quoteId === quoteId) {
          toast({ 
            title: 'Payment received', 
            description: `Successfully received ${invoiceAmount} sats!` 
          });
          setShowInvoiceModal(false);
          setCurrentInvoice(null);
          mintQuoteUnsubscribeRef.current = null;
          unsubscribe();
        }
      });

      mintQuoteUnsubscribeRef.current = unsubscribe;
    } catch (err) {
      const raw = err instanceof Error ? err.message : 'Unknown error';
      // A CORS-blocked or unreachable mint surfaces as a NetworkError /
      // "Failed to fetch mint". Give the user something actionable instead of a
      // dead end — the fix is to switch to a browser-reachable mint.
      const looksUnreachable =
        /failed to fetch|networkerror|load failed|cors|fetch mint/i.test(raw);
      toast({
        variant: 'destructive',
        title: looksUnreachable ? "Can't reach this mint" : 'Failed to create invoice',
        description: looksUnreachable
          ? `${activeMintUrl?.replace(/^https?:\/\//, '')} can't be reached from your browser (no CORS). Switch to a web-friendly mint in wallet settings — or use the native app, where any mint works.`
          : raw,
      });
    } finally {
      setIsGeneratingInvoice(false);
    }
  }, [coco, quoteAmount, activeMintUrl, generateQRCode, isGeneratingInvoice, toast]);

  // Handle send (pay Lightning invoice)
  const handleLightningSend = useCallback(async () => {
    if (isProcessingPayment || !coco || !lightningInvoice || !activeMintUrl) return;

    setIsProcessingPayment(true);

    try {
      const invoice = lightningInvoice.trim();
      
      // Create and pay melt quote
      const meltQuote = await coco.quotes.createMeltQuote(activeMintUrl, invoice);
      await coco.quotes.payMeltQuote(activeMintUrl, meltQuote.quote);

      toast({ 
        title: 'Payment sent', 
        description: 'Lightning payment completed successfully' 
      });

      setLightningInvoice('');
    } catch (err) {
      const raw = err instanceof Error ? err.message : 'Unknown error';
      const looksUnreachable =
        /failed to fetch|networkerror|load failed|cors|fetch mint/i.test(raw);
      toast({
        variant: 'destructive',
        title: looksUnreachable ? "Can't reach this mint" : 'Payment failed',
        description: looksUnreachable
          ? `${activeMintUrl?.replace(/^https?:\/\//, '')} can't be reached from your browser. Switch to a different mint in wallet settings and try again.`
          : raw,
      });
    } finally {
      setIsProcessingPayment(false);
    }
  }, [coco, lightningInvoice, activeMintUrl, isProcessingPayment, toast]);

  // Cleanup listener on unmount
  useCallback(() => {
    return () => {
      if (mintQuoteUnsubscribeRef.current) {
        mintQuoteUnsubscribeRef.current();
      }
    };
  }, []);

  return {
    // Receive
    quoteAmount,
    setQuoteAmount,
    showInvoiceModal,
    setShowInvoiceModal,
    currentInvoice,
    setCurrentInvoice,
    handleLightningReceive,
    isGeneratingInvoice,
    
    // Send
    lightningInvoice,
    setLightningInvoice,
    isProcessingPayment,
    handleLightningSend,
  };
}
